import { useMemo, type ReactNode } from "react";
import { ChevronLeft, CircleCheck, CircleDot, Info } from "lucide-react";
import { useGroomingMetrics } from "@/hooks/queries/use-grooming";
import type { GroomingBacklog, GroomingMetrics, GroomingSizedItem } from "@/lib/api/grooming";
import {
  LANDED_WORDS,
  SIZING_ITEMS_NOTE,
  dayWords,
  noSessionsLine,
  pointsWord,
  preparationItemsHeading,
  preparationItemsSubtitle,
  rangeWords,
  sessionDay,
  sessionsNewestFirst,
  sessionGroupLine,
  sizedItemName,
  sizingItemsHeading,
  sizingItemsSubtitle,
  sprintItemsHeading,
  withBacklogSprint,
  type ProgressItemsKind,
  type ProgressWindow,
} from "@/lib/grooming/grooming-progress";
import { STAGE_LABELS } from "@/lib/grooming/plain-words";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";
import {
  CopiedSummary,
  CopySummaryButton,
  SECONDARY_BUTTON,
  WindowSwitch,
  useCopySummary,
} from "./grooming-progress-panel";

/**
 * The items behind a "How grooming is going" line (epic PI-SPEC-SW-E293,
 * feature PI-SPEC-SW-1505, UX-PM-01, frame E2): a Backlog content view chosen
 * by `?progress=sizing|preparation|sprint`, in place of the tiles, the
 * exception strip and the list while it shows. Six columns do not fit the
 * 380 px panel at 1180x740, so the view spans the content area and the link
 * can be shared and the back button works.
 *
 * - Sizing: exactly the metrics read's sized items, with Claude's range and
 *   middle guess, the team's points, where they landed (icon and word, never
 *   colour alone, never red) and when the team sized them. Claude's size
 *   reaches the web only for sized items (01-roles.md section 9.4).
 * - Preparation: each finished session's agenda items and whether each had a
 *   brief before the meeting.
 * - This sprint: the Backlog read's exceptions (no API change).
 *
 * Every row opens the item in the reading pane through `onOpenItem`.
 */

const SIZING_GRID = "grid-cols-[62px_minmax(0,1fr)_130px_104px_160px_92px]";
const PREPARATION_GRID = "grid-cols-[62px_minmax(0,1fr)_220px]";
const SPRINT_GRID = "grid-cols-[62px_minmax(0,1fr)_160px]";

const HEADER_ROW = cn(
  "grid h-[34px] items-center gap-x-3 bg-[#0E0E10] px-4 text-[11px] font-semibold uppercase tracking-[0.08em]",
  TOOLBOX_TEXT.faint,
);
const ITEM_ROW =
  "grid w-full items-center gap-x-3 border-t border-[#141417] px-4 py-2.5 text-left text-[13px] transition-colors hover:bg-[#141417] focus-visible:bg-[#141417] focus-visible:outline-none";

function TableHeader({ grid, headings }: { grid: string; headings: readonly string[] }) {
  return (
    <div className={cn(HEADER_ROW, grid)} aria-hidden="true">
      {headings.map((heading) => (
        <span key={heading} className="truncate">
          {heading}
        </span>
      ))}
    </div>
  );
}

function ItemId({ itemId }: { itemId: string }) {
  return <span className="truncate font-mono text-xs text-muted-foreground">{itemId}</span>;
}

function ItemTitle({ children }: { children: string }) {
  return (
    <span className="truncate text-foreground" title={children}>
      {children}
    </span>
  );
}

/** Icon and word: a green check for "yes", a grey dot for "no" (never red, never colour alone). */
function Mark({ yes, children }: { yes: boolean; children: ReactNode }) {
  const Icon = yes ? CircleCheck : CircleDot;
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5 text-foreground">
      <Icon className={cn("h-3.5 w-3.5 flex-none", yes ? "text-[#4ADE80]" : "text-[#A1A1AA]")} aria-hidden="true" />
      <span className="truncate">{children}</span>
    </span>
  );
}

function SizedRow({ item, name, onOpen }: { item: GroomingSizedItem; name: string; onOpen: () => void }) {
  return (
    <li>
      <button
        type="button"
        className={cn(ITEM_ROW, SIZING_GRID)}
        data-testid="progress-item-row"
        data-item-id={item.itemId}
        data-landed={item.landed}
        onClick={onOpen}
      >
        <ItemId itemId={item.itemId} />
        <ItemTitle>{name}</ItemTitle>
        {/* The column headings are hidden from screen readers, so each value carries its own name. */}
        <span className="truncate">
          <span className="sr-only">Claude's range: </span>
          <span className="text-muted-foreground">{rangeWords(item)}</span>{" "}
          <span className={TOOLBOX_TEXT.faint}>(about {String(item.suggestedPoints)})</span>
        </span>
        <span className="truncate font-semibold text-foreground">
          <span className="sr-only">Team: </span>
          {pointsWord(item.teamPoints)}
        </span>
        <Mark yes={item.landed === "inside"}>
          <span className="sr-only">Where it landed: </span>
          {LANDED_WORDS[item.landed]}
        </Mark>
        <span className="truncate text-xs text-muted-foreground">
          <span className="sr-only">Sized: </span>
          {dayWords(item.teamPointsSeenAt) || "—"}
        </span>
      </button>
    </li>
  );
}

function SizingTable({
  metrics,
  backlogTitles,
  onOpenItem,
}: {
  metrics: GroomingMetrics;
  backlogTitles: ReadonlyMap<string, string>;
  onOpenItem: (itemId: string) => void;
}) {
  const items = metrics.estimateAgreement.items;
  return (
    <div className="glass-card overflow-hidden rounded-[10px]" data-testid="progress-items-table">
      <TableHeader grid={SIZING_GRID} headings={["ID", "Item", "Claude's range", "Team", "Where it landed", "Sized"]} />
      {items.length === 0 ? (
        <p className="border-t border-[#141417] px-4 py-3 text-[13px] text-muted-foreground">No items sized yet.</p>
      ) : (
        <ul>
          {items.map((item) => (
            <SizedRow
              key={item.itemId}
              item={item}
              name={sizedItemName(item, backlogTitles.get(item.itemId))}
              onOpen={() => {
                onOpenItem(item.itemId);
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function PreparationTable({
  metrics,
  sinceDays,
  backlogTitles,
  onOpenItem,
}: {
  metrics: GroomingMetrics;
  sinceDays: number;
  backlogTitles: ReadonlyMap<string, string>;
  onOpenItem: (itemId: string) => void;
}) {
  const batches = sessionsNewestFirst(metrics.prepCoverage.batches);
  return (
    <div className="glass-card overflow-hidden rounded-[10px]" data-testid="progress-items-table">
      <TableHeader grid={PREPARATION_GRID} headings={["ID", "Item", "Before the meeting"]} />
      {batches.length === 0 ? (
        <p className="border-t border-[#141417] px-4 py-3 text-[13px] text-muted-foreground">{noSessionsLine(sinceDays)}</p>
      ) : (
        batches.map((batch) => (
          <section key={batch.batchId} data-testid="progress-session-group" aria-label={`Session ${sessionDay(batch)}`}>
            <p className="flex gap-2 border-t border-[#1F1F23] bg-[#0B0B0D] px-4 py-2 text-xs">
              <span className="font-semibold text-foreground">{sessionDay(batch)}</span>
              <span className="text-muted-foreground">{sessionGroupLine(batch)}</span>
            </p>
            <ul>
              {batch.agendaItems.map((item) => (
                <li key={item.itemId}>
                  <button
                    type="button"
                    className={cn(ITEM_ROW, PREPARATION_GRID)}
                    data-testid="progress-item-row"
                    data-item-id={item.itemId}
                    data-prepared={item.prepared ? "yes" : "no"}
                    onClick={() => {
                      onOpenItem(item.itemId);
                    }}
                  >
                    <ItemId itemId={item.itemId} />
                    <ItemTitle>{sizedItemName(item, backlogTitles.get(item.itemId))}</ItemTitle>
                    <Mark yes={item.prepared}>
                      {item.prepared ? "Brief before the meeting" : "No brief before the meeting"}
                    </Mark>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

function SprintTable({ backlog, onOpenItem }: { backlog: GroomingBacklog; onOpenItem: (itemId: string) => void }) {
  const sprint = backlog.currentIteration?.name;
  return (
    <div className="glass-card overflow-hidden rounded-[10px]" data-testid="progress-items-table">
      <TableHeader grid={SPRINT_GRID} headings={["ID", "Item", "Stage"]} />
      {backlog.exceptions.length === 0 ? (
        <p className="border-t border-[#141417] px-4 py-3 text-[13px] text-muted-foreground">
          {sprint ? `Nothing entered ${sprint} ungroomed.` : "The team has no current sprint."}
        </p>
      ) : (
        <ul>
          {backlog.exceptions.map((item) => (
            <li key={item.itemId}>
              <button
                type="button"
                className={cn(ITEM_ROW, SPRINT_GRID)}
                data-testid="progress-item-row"
                data-item-id={item.itemId}
                onClick={() => {
                  onOpenItem(item.itemId);
                }}
              >
                <ItemId itemId={item.itemId} />
                <ItemTitle>{item.title || `AB#${item.itemId}`}</ItemTitle>
                <span className="truncate text-muted-foreground">{STAGE_LABELS[item.stage]}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export interface GroomingProgressItemsProps {
  project: string;
  kind: ProgressItemsKind;
  /** The panel's window; the switch here changes the same state. */
  sinceDays: ProgressWindow;
  onSinceDaysChange: (days: ProgressWindow) => void;
  /** The Backlog read: its exceptions are the sprint view's rows. */
  backlog: GroomingBacklog;
  /** "How grooming is going": back to the panel (clears `?progress`). */
  onBack: () => void;
  /** A row: opens the item in the reading pane. */
  onOpenItem: (itemId: string) => void;
  /** The plain text Copy summary puts on the clipboard (frame E3). */
  summaryFor: (metrics: GroomingMetrics) => string;
  /** False while the Backlog's own read is in flight, so the team points it records are counted. */
  enabled?: boolean;
}

/** The items behind a line (frame E2). */
export function GroomingProgressItems({
  project,
  kind,
  sinceDays,
  onSinceDaysChange,
  backlog,
  onBack,
  onOpenItem,
  summaryFor,
  enabled = true,
}: GroomingProgressItemsProps) {
  const metrics = useGroomingMetrics(project, sinceDays, { enabled });
  // This sprint counts the Backlog read's exceptions: the rows this view lists.
  const data = metrics.data && withBacklogSprint(metrics.data, backlog.exceptions.length);
  const copy = useCopySummary();
  const windowed = kind !== "sprint";
  // A brief written outside a Toolbox run has no batch title; the loaded Backlog usually has it.
  const backlogTitles = useMemo(
    () => new Map([...backlog.items, ...backlog.exceptions].map((item) => [item.itemId, item.title])),
    [backlog]
  );

  const sprint = sprintItemsHeading(backlog.currentIteration, backlog.exceptions.length);
  const heading =
    kind === "sprint"
      ? sprint.heading
      : data
        ? kind === "sizing"
          ? sizingItemsHeading(data.estimateAgreement)
          : preparationItemsHeading(data.prepCoverage)
        : kind === "sizing"
          ? "Sizing"
          : "Preparation";
  const subtitle =
    kind === "sprint"
      ? sprint.subtitle
      : kind === "sizing"
        ? sizingItemsSubtitle(sinceDays)
        : preparationItemsSubtitle(sinceDays);

  let table: ReactNode;
  if (kind === "sprint") {
    table = <SprintTable backlog={backlog} onOpenItem={onOpenItem} />;
  } else if (data) {
    table =
      kind === "sizing" ? (
        <SizingTable metrics={data} backlogTitles={backlogTitles} onOpenItem={onOpenItem} />
      ) : (
        <PreparationTable metrics={data} sinceDays={sinceDays} backlogTitles={backlogTitles} onOpenItem={onOpenItem} />
      );
  } else if (metrics.isError) {
    table = (
      <div className="glass-card flex flex-col items-start gap-2.5 rounded-[10px] px-4 py-4" data-testid="progress-items-error">
        <p className={TOOLBOX_TEXT.content}>How grooming is going could not be read.</p>
        <button
          type="button"
          className={SECONDARY_BUTTON}
          disabled={metrics.isFetching}
          onClick={() => {
            void metrics.refetch();
          }}
        >
          {metrics.isFetching ? "Trying again…" : "Try again"}
        </button>
      </div>
    );
  } else {
    table = (
      <div className="flex min-h-[160px] items-center justify-center" role="status" aria-live="polite">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="sr-only">Reading how grooming is going…</span>
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-3" data-testid="progress-items" data-kind={kind} aria-label={heading}>
      <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <button
            type="button"
            className="inline-flex items-center gap-1 self-start text-xs text-muted-foreground hover:text-foreground"
            data-testid="progress-items-back"
            onClick={onBack}
          >
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
            How grooming is going
          </button>
          <h2 className="font-display text-[22px] font-semibold leading-tight text-foreground" data-testid="progress-items-heading">
            {heading}
          </h2>
          {subtitle && (
            <p className="text-xs text-muted-foreground" data-testid="progress-items-subtitle">
              {subtitle}
            </p>
          )}
        </div>
        {windowed && (
          <WindowSwitch
            value={sinceDays}
            onChange={(days) => {
              copy.dismiss();
              onSinceDaysChange(days);
            }}
          />
        )}
      </div>

      {table}

      <div className="mt-0.5 flex items-start gap-3">
        <p className="flex min-w-0 flex-1 items-start gap-2 text-xs text-muted-foreground">
          {kind === "sizing" && (
            <>
              <Info className="mt-px h-3.5 w-3.5 flex-none" aria-hidden="true" />
              <span data-testid="progress-items-note">{SIZING_ITEMS_NOTE}</span>
            </>
          )}
        </p>
        <CopySummaryButton
          justCopied={copy.justCopied}
          disabled={!data}
          onClick={() => {
            if (data) void copy.copy(summaryFor(data));
          }}
        />
      </div>
      {copy.outcome && (
        <div className="glass-card max-w-[560px] self-end rounded-[10px]">
          <CopiedSummary outcome={copy.outcome} onBack={copy.dismiss} />
        </div>
      )}
    </section>
  );
}
